import React from 'react';
import buildContext from '~/components/jsx-helpers/build-context';
import { useUserModel } from '~/models/usermodel';
import useMyOpenStaxUser from '~/models/myopenstax-user';
import { useRefreshable } from '~/helpers/data';
import debounce from 'lodash/debounce';
const debouncedDebug = debounce((...args) => console.debug(...args), 100);
function checkUserForProblems(user) {
    if (!user || Reflect.ownKeys(user).length === 0) {
        debouncedDebug('No user info retrieved');
    }
    else if (!('pending_verification' in user)) {
        debouncedDebug('No pending_verification flag set in user info', user);
    }
}
// eslint-disable-next-line complexity
function getUserStatus(user) {
    var _a, _b, _c;
    const isInstructor = user.username && 'groups' in user && ((_a = user.groups) === null || _a === void 0 ? void 0 : _a.includes('Faculty'));
    const isStudent = user.username && !isInstructor;
    const trackDownloads = ((_b = user.accountsModel) === null || _b === void 0 ? void 0 : _b.faculty_status) === 'confirmed_faculty';
    checkUserForProblems(user);
    return {
        isInstructor,
        isStudent,
        pendingVerification: user.pending_verification,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        userInfo: user,
        school: (_c = user.accountsModel) === null || _c === void 0 ? void 0 : _c.school_name,
        uuid: user.uuid,
        trackDownloads
    };
}
function useContextValue() {
    var _a;
    const model = useUserModel();
    const userStatus = React.useMemo(() => getUserStatus(model), [model]);
    const isVerified = ((_a = model === null || model === void 0 ? void 0 : model.accountsModel) === null || _a === void 0 ? void 0 : _a.faculty_status) === 'confirmed_faculty';
    const [fetchTime, updateMyOpenStaxUser] = useRefreshable(() => Date.now());
    const myOpenStaxUser = useMyOpenStaxUser(isVerified, fetchTime);
    const value = React.useMemo(() => (model === null || model === void 0 ? void 0 : model.last_name)
        ? {
            accountId: model.id,
            userName: `${model.first_name} ${model.last_name.substr(0, 1)}.`,
            userModel: model,
            uuid: model.uuid,
            isVerified,
            userStatus,
            myOpenStaxUser,
            updateMyOpenStaxUser
        }
        : { userStatus, myOpenStaxUser }, [model, userStatus, isVerified, myOpenStaxUser, updateMyOpenStaxUser]);
    React.useEffect(() => {
        var _a;
        const w = window;
        if (model && model.id) {
            (_a = w.pi) === null || _a === void 0 ? void 0 : _a.call(w, 'identify_client', model.id);
        }
    }, [model]);
    return value;
}
const { useContext, ContextProvider } = buildContext({ useContextValue });
export { useContext as default, ContextProvider as UserContextProvider };
